import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { FormControl, FormControlStatus, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DrillScoring, TranslationService } from '@core';
import { DrillType } from '@drills/infra';
import {
  SzBottomSheetComponent,
  SzButtonComponent,
  SzIconButtonComponent,
  SzIconComponent,
  SzNumFocalComponent,
  SzNumpadGridComponent,
  SzNumpadKey,
  SzRadioCardComponent,
  SzSectionHeadComponent,
  SzSegmentedComponent,
  SzSegmentedOption,
  SzTextInputComponent,
  SzTextareaComponent,
  SzToggleComponent
} from '@ui';
import { drillScoringLabelMapper, drillScoringShortMapper } from '../../application/mappers';
import { DrillFormValue } from './drill-form-value';

interface DrillFormControls {
  name: FormControl<string>;
  type: FormControl<DrillType>;
  scoring: FormControl<DrillScoring | null>;
  shotsCount: FormControl<string>;
  description: FormControl<string | null>;
  favorite: FormControl<boolean>;
}

interface ScoringOption {
  readonly key: DrillScoring;
  readonly label: string;
  readonly description: string;
}

const NAME_MAX: number = 60;
const DESCRIPTION_MAX: number = 500;
const SHOTS_COUNT_MIN: number = 1;
const SHOTS_COUNT_MAX: number = 99;
const SHOTS_DIGITS_MAX: number = 2;

@Component({
  selector: 'lib-drill-form',
  templateUrl: './drill-form.component.html',
  styleUrls: ['./drill-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    SzBottomSheetComponent,
    SzButtonComponent,
    SzIconButtonComponent,
    SzIconComponent,
    SzNumFocalComponent,
    SzNumpadGridComponent,
    SzSectionHeadComponent,
    SzSegmentedComponent,
    SzTextInputComponent,
    SzTextareaComponent,
    SzRadioCardComponent,
    SzToggleComponent
  ]
})
export class DrillFormComponent {
  readonly initialValue: InputSignal<DrillFormValue | null> = input<DrillFormValue | null>(null);
  readonly formSubmit: OutputEmitterRef<DrillFormValue> = output<DrillFormValue>();

  private readonly translation: TranslationService = inject(TranslationService);
  private readonly translate: TranslateService = inject(TranslateService);

  readonly form: FormGroup<DrillFormControls> = new FormGroup<DrillFormControls>({
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(NAME_MAX)]
    }),
    type: new FormControl<DrillType>(DrillType.Dry, { nonNullable: true, validators: [Validators.required] }),
    scoring: new FormControl<DrillScoring | null>(null, { validators: [Validators.required] }),
    shotsCount: new FormControl<string>('', { nonNullable: true }),
    description: new FormControl<string | null>(null, { validators: [Validators.maxLength(DESCRIPTION_MAX)] }),
    favorite: new FormControl<boolean>(false, { nonNullable: true })
  });

  readonly nameMax: number = NAME_MAX;
  readonly descriptionMax: number = DESCRIPTION_MAX;
  readonly shotsCountMin: number = SHOTS_COUNT_MIN;
  readonly shotsCountMax: number = SHOTS_COUNT_MAX;

  readonly typeOptions: Signal<ReadonlyArray<SzSegmentedOption<DrillType>>> = computed(() => {
    this.translation.translationsLoaded();
    return [
      { label: this.translation.translate('common.drillType.dry'), value: DrillType.Dry },
      { label: this.translation.translate('common.drillType.live'), value: DrillType.Live }
    ];
  });

  readonly scoringOptions: Signal<ReadonlyArray<ScoringOption>> = computed(() => {
    this.translation.translationsLoaded();
    return [DrillScoring.TimePerRep, DrillScoring.RepsOnly, DrillScoring.HitFactor].map(
      (s: DrillScoring): ScoringOption => ({
        key: s,
        label: drillScoringLabelMapper(s),
        description: `${drillScoringShortMapper(s)} · ${this.translation.translate(`drills.form.scoring.${s}.desc`)}`
      })
    );
  });

  readonly currentType: Signal<DrillType> = toSignal(this.form.controls.type.valueChanges, {
    initialValue: this.form.controls.type.value
  });
  readonly currentScoring: Signal<DrillScoring | null> = toSignal(this.form.controls.scoring.valueChanges, {
    initialValue: this.form.controls.scoring.value
  });
  readonly currentName: Signal<string> = toSignal(this.form.controls.name.valueChanges, {
    initialValue: this.form.controls.name.value
  });

  readonly isHitFactor: Signal<boolean> = computed(() => this.currentScoring() === DrillScoring.HitFactor);

  private readonly formStatus: Signal<FormControlStatus> = toSignal(this.form.statusChanges, {
    initialValue: this.form.status
  });
  readonly canSubmit: Signal<boolean> = computed((): boolean => this.formStatus() === 'VALID');

  readonly shotsSheetOpen: WritableSignal<boolean> = signal<boolean>(false);
  readonly shotsDigits: WritableSignal<string> = signal<string>('');

  private readonly currentShotsCountRaw: Signal<string> = toSignal(this.form.controls.shotsCount.valueChanges, {
    initialValue: this.form.controls.shotsCount.value
  });

  readonly shotsCountValue: Signal<number | null> = computed((): number | null => {
    const raw: string = this.currentShotsCountRaw();
    if (raw === '') return null;
    const parsed: number = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return null;
    if (parsed < SHOTS_COUNT_MIN || parsed > SHOTS_COUNT_MAX) return null;
    return parsed;
  });

  readonly shotsDraftValue: Signal<number> = computed((): number => {
    const d: string = this.shotsDigits();
    return d.length === 0 ? 0 : Number.parseInt(d, 10);
  });

  readonly shotsDraftDisplay: Signal<string> = computed(() => this.shotsDraftValue().toString());

  readonly canSaveShots: Signal<boolean> = computed((): boolean => {
    const v: number = this.shotsDraftValue();
    return v >= SHOTS_COUNT_MIN && v <= SHOTS_COUNT_MAX;
  });

  readonly numpadKeys: Signal<readonly SzNumpadKey[]> = computed((): readonly SzNumpadKey[] => [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '00', value: '00', variant: 'fn' },
    { label: '0', value: '0' },
    { label: this.translate.instant('session.numpad.backspace'), value: 'del', variant: 'fn', icon: 'arrow-back' }
  ]);

  constructor() {
    this.patchOnInitialValueChange();
    this.syncShotsCountValidatorsToScoring();
  }

  setType(value: DrillType): void {
    this.form.controls.type.setValue(value);
  }

  setScoring(value: DrillScoring): void {
    this.form.controls.scoring.setValue(value);
  }

  openShotsSheet(): void {
    const current: number | null = this.shotsCountValue();
    this.shotsDigits.set(current === null ? '' : current.toString());
    this.shotsSheetOpen.set(true);
  }

  closeShotsSheet(): void {
    this.shotsSheetOpen.set(false);
  }

  saveShots(): void {
    if (!this.canSaveShots()) return;
    this.form.controls.shotsCount.setValue(this.shotsDraftValue().toString());
    this.shotsSheetOpen.set(false);
  }

  onShotsKeyPress(value: string): void {
    if (value === 'del') {
      this.shotsDigits.update((cur: string) => cur.slice(0, -1));
      return;
    }
    if (value === '00') {
      if (this.shotsDigits().length + 2 > SHOTS_DIGITS_MAX) return;
      this.shotsDigits.update((cur: string) => (cur === '' ? '0' : cur + '00'));
      return;
    }
    this.shotsDigits.update((cur: string) => (cur.length >= SHOTS_DIGITS_MAX ? cur : cur + value));
  }

  submit(): void {
    if (this.form.invalid) return;
    const raw: {
      name: string;
      type: DrillType;
      scoring: DrillScoring | null;
      shotsCount: string;
      description: string | null;
      favorite: boolean;
    } = this.form.getRawValue();
    if (raw.scoring === null) return;
    const shotsCount: number | null =
      raw.scoring === DrillScoring.HitFactor ? this.parseShotsCount(raw.shotsCount) : null;
    if (raw.scoring === DrillScoring.HitFactor && shotsCount === null) return;
    this.formSubmit.emit({
      name: raw.name,
      type: raw.type,
      scoring: raw.scoring,
      shotsCount,
      description: raw.description,
      favorite: raw.favorite
    });
  }

  private parseShotsCount(raw: string): number | null {
    const parsed: number = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return null;
    if (parsed < SHOTS_COUNT_MIN || parsed > SHOTS_COUNT_MAX) return null;
    return parsed;
  }

  private patchOnInitialValueChange(): void {
    effect(() => {
      const v: DrillFormValue | null = this.initialValue();
      if (v !== null) {
        this.form.patchValue({
          name: v.name,
          type: v.type,
          scoring: v.scoring,
          shotsCount: v.shotsCount === null ? '' : v.shotsCount.toString(),
          description: v.description,
          favorite: v.favorite
        });
      }
    });
  }

  private syncShotsCountValidatorsToScoring(): void {
    effect(() => {
      const scoring: DrillScoring | null = this.currentScoring();
      const control: FormControl<string> = this.form.controls.shotsCount;
      if (scoring === DrillScoring.HitFactor) {
        control.setValidators([
          Validators.required,
          Validators.pattern(/^\d+$/),
          Validators.min(SHOTS_COUNT_MIN),
          Validators.max(SHOTS_COUNT_MAX)
        ]);
      } else {
        control.clearValidators();
        if (control.value !== '') control.setValue('');
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }
}
